import {
  FC, useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'next-i18next';
import Checkbox from 'src/common/components/Checkbox';
import SearchField from 'src/admin/listing-editor/components/SearchField';
import { useForm, useFormState } from 'react-final-form';
import type { SnoozeFormState } from 'src/admin/common/components/SnoozeModal/types';
import { SELECTED_LISTING_IDS_FIELD } from 'src/admin/common/components/SnoozeModal/constants';
import getClassNames from 'src/common/utils/getClassNames';
import classNames from 'classnames';
import useListingSnooze from 'src/admin/listing-editor/hooks/useListingSnooze';
import WizardNavigation from 'src/admin/common/components/SnoozeModal/WizardNavigation';
import type { MinimalEditorListing } from 'src/common/types/api/listings';
import BulkList from './BulkList';
import styles from './BulkListingSelector.module.scss';

export type BulkListingSelectorProps = {};

const BulkListingSelector: FC<BulkListingSelectorProps> = () => {
  const { t } = useTranslation('admin-listings');

  const { filterListings } = useListingSnooze();
  const { change } = useForm<SnoozeFormState>();
  const { values } = useFormState<SnoozeFormState>();

  const selectedIds = useMemo(
    () => values.boats ?? [],
    [values.boats],
  );

  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [listings, setListings] = useState<MinimalEditorListing[]>([]);

  const visibleSelectionIds = useMemo(() => {
    const visibleListings = new Set(listings.map(({ id }) => id));
    const visibleSelection = selectedIds.filter((id) => (
      visibleListings.has(id)
    ));
    return visibleSelection;
  }, [listings, selectedIds]);

  const allSelected = useMemo(() => (
    visibleSelectionIds.length === listings.length && visibleSelectionIds.length > 0
  ), [listings.length, visibleSelectionIds.length]);

  const someSelected = useMemo(() => (
    visibleSelectionIds.length > 0 && !allSelected
  ), [visibleSelectionIds.length, allSelected]);

  useEffect(() => {
    (async function getList() {
      setLoading(true);
      const response = await filterListings(search);
      setListings(response);
      setLoading(false);
    }());
  }, [filterListings, search]);

  const toggleSelectAll = useCallback(() => {
    const visibleListings = listings.map(({ id }) => id);
    if (!allSelected) {
      // Add visible to selection
      const set = new Set([...selectedIds, ...visibleListings]);
      change(SELECTED_LISTING_IDS_FIELD, Array.from(set));
    } else {
      // Only deselect the currently visible listings
      const set = new Set(visibleListings);
      const nonVisible = selectedIds.filter((id) => !set.has(id));
      change(SELECTED_LISTING_IDS_FIELD, nonVisible);
    }
  }, [allSelected, change, listings, selectedIds]);

  return (
    <>
      <WizardNavigation>
        {t('snooze-listing--bulk-select-title')}
      </WizardNavigation>
      <header className={styles.header}>
        <div className={styles.selectAll}>
          <label className={styles.label}>
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={() => toggleSelectAll()}
              disabled={loading}
            />
            <span className={styles.selectAllLabel}>
              {t('snooze-listing--bulk-selection--select-all')}
            </span>
          </label>
        </div>
        <div className={getClassNames(styles, 'search', classNames({ inset: true }))}>
          <SearchField
            onChange={setSearch}
            theme="shade"
            delayChange
            searching={loading}
          />
        </div>
      </header>
      <BulkList
        listings={listings}
        loading={loading}
      />
    </>
  );
};

export default BulkListingSelector;
